import { formatShortDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type Theme, Tooltip } from "@mui/material";
import { useAgentProfilePicture } from "@src/appV2/ShiftBlock/hooks/useGetAgentProfilePicture";

import { Avatar } from "../../../components/Avatar";
import { Pill } from "../../../components/Pill";
import { SelectableListItem } from "../../../components/SelectableListItem/SelectableListItem";
import { Text } from "../../../components/Text";
import { type Professional, WORKER_STATUS, type WorkerStatus } from "./types";

const BLOCKED_TOOLTIP =
  "This professional has been blocked by your workplace and cannot be invited.";
const RESTRICTED_TOOLTIP = "Reports of poor attendance or unprofessional conduct.";

const STATUS_COPY: Record<WorkerStatus, string> = {
  [WORKER_STATUS.blocked]: "Blocked Professional",
  [WORKER_STATUS.restricted]: "Attendance Warning",
};

interface StatusBadgeProps {
  status: WorkerStatus;
}

/**
 * Inline badge rendered on each professional row that carries a workplace
 * `statusLabel`. Blocked uses the error token; restricted uses the warning
 * token. Favorites are communicated via the starred section header, not here.
 */
function StatusBadge(props: StatusBadgeProps) {
  const { status } = props;
  const isBlocked = status === WORKER_STATUS.blocked;

  return (
    <Pill
      size="small"
      sx={(theme: Theme) => ({
        // Status tokens keep the badge inside the design system instead of
        // reaching into theme.palette.error/warning directly.
        backgroundColor: isBlocked
          ? theme.palette.statusChip?.error.background
          : theme.palette.statusChip?.warning.background,
        color: isBlocked
          ? theme.palette.statusChip?.error.text
          : theme.palette.statusChip?.warning.text,
        border: "none",
      })}
    >
      <Text semibold sx={{ color: "inherit", whiteSpace: "nowrap" }} variant="caption">
        {STATUS_COPY[status]}
      </Text>
    </Pill>
  );
}

interface ProfessionalListItemProps {
  professional: Professional;
  isSelected: boolean;
  onSelect: (professional: Professional) => void;
  disabled?: boolean;
}

/**
 * A professional list item for the invite drawer.
 * Displays avatar, name, and "Worked N shifts" subtitle for professionals with shift history.
 * When the professional has a blocked/restricted `statusLabel`, a status badge
 * renders inline. Blocked rows are disabled from selection and carry an
 * explanatory tooltip (ORB-1749).
 */
export function ProfessionalListItem(props: ProfessionalListItemProps) {
  const { professional, isSelected, onSelect, disabled = false } = props;
  const { statusLabel } = professional;
  const isBlocked = statusLabel === WORKER_STATUS.blocked;
  const effectiveDisabled = disabled || isBlocked;

  const { data: profileImageData } = useAgentProfilePicture(
    { agentId: professional.id, size: "small" },
    { enabled: !isDefined(professional.avatarUrl) }
  );

  const initials = professional.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const avatarSource = professional.avatarUrl ?? profileImageData?.distribution_url;

  const row = (
    <SelectableListItem
      disabled={effectiveDisabled}
      indicatorPosition="left"
      isSelected={isSelected}
      selectionMode="radio"
      onSelect={() => {
        if (isBlocked) {
          return;
        }
        onSelect(professional);
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: 1 }}>
        <Avatar size="medium" src={avatarSource}>
          {initials}
        </Avatar>

        <Stack sx={{ display: "flex", flex: 1, gap: "0.25rem", minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", flexWrap: "wrap", rowGap: "0.25rem" }}
          >
            <Text
              semibold
              sx={(theme) => ({
                color: theme.palette.text.primary,
              })}
              variant="body2"
            >
              {professional.name}
            </Text>
            {isDefined(statusLabel) && <StatusBadge status={statusLabel} />}
          </Stack>

          {!professional.isStarred && isDefined(professional.lastShiftDate) && (
            <Text
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
              variant="caption"
            >
              Last shift: {formatShortDate(professional.lastShiftDate)}
            </Text>
          )}

          {professional.isStarred &&
            isDefined(professional.shiftsWorked) &&
            professional.shiftsWorked > 0 && (
              <Text
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
                variant="caption"
              >
                Worked {professional.shiftsWorked} shift
                {professional.shiftsWorked === 1 ? "" : "s"}
              </Text>
            )}
        </Stack>
      </Stack>
    </SelectableListItem>
  );

  if (!isDefined(statusLabel)) {
    return row;
  }

  const tooltipTitle = statusLabel === WORKER_STATUS.blocked ? BLOCKED_TOOLTIP : RESTRICTED_TOOLTIP;

  if (isBlocked) {
    // Wrap in a span so the tooltip has a focusable target even though the
    // underlying button is disabled (MUI requires a non-disabled wrapper for
    // disabled children tooltips).
    return (
      <Tooltip arrow title={tooltipTitle}>
        <span aria-disabled="true" style={{ display: "block" }}>
          {row}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip arrow title={tooltipTitle}>
      <span style={{ display: "block" }}>{row}</span>
    </Tooltip>
  );
}
